import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  List,
  Tag,
  Avatar,
  Button,
  Input,
  message,
  Typography,
  Divider,
} from "antd";
import {
  UserOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  CopyOutlined,
  LinkOutlined,
  BankOutlined,
  CreditCardOutlined,
  TransactionOutlined,
  SendOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../hooks/auth/AuthContext";
import "./Dashboard.less";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useQuery } from "@apollo/client";
import {
  GET_CURRENT_USER_WALLET,
  GET_WITHDRAWAL_HISTORY,
  GET_PENDING_POINT,
  GET_TOTAL_INCENTIVE_POINTS,
} from "../../shared/graphql/wallet.gql";
import { GET_MY_TEAM } from "../../shared/graphql/user.gql";
import { getConfigurationsFromSession } from "../configuration/Configuration";
const { Text } = Typography;

const Dashboard = () => {
  const { authState } = useAuth();
  const intl = useIntl();
  const [copyButtonText, setCopyButtonText] = useState("COPY_LINK");
  const navigate = useNavigate();
  const {
    data: walletTransactionsData,
    loading: walletTransactionsLoading,
    error: walletTransactionsError,
  } = useQuery(GET_CURRENT_USER_WALLET);
  const {
    data: pendingPointsData,
    loading: pendingPointsLoading,
    error: pendingPointsError,
  } = useQuery(GET_PENDING_POINT);
  const {
    data: withdrawalHistoryData,
    loading: withdrawalHistoryLoading,
    error: withdrawalHistoryError,
  } = useQuery(GET_WITHDRAWAL_HISTORY);
  const {
    data: teamData,
    loading: teamLoading,
    error: teamError,
  } = useQuery(GET_MY_TEAM);
  const {
    data: totalIncentivePointsData,
    loading: totalIncentivePointsLoading,
    error: totalIncentivePointsError,
  } = useQuery(GET_TOTAL_INCENTIVE_POINTS);
  // Generate referral link
  const tableData =
    withdrawalHistoryData?.getWithdrawalHistory?.walletTransactions || [];
  const formatedTableData = tableData.map((item: any) => {
    // Determine the correct transaction type display
    let displayType;
    switch(item.type) {
      case "REFERRAL_BONUS":
        displayType = "Referral Bonus";
        break;
      case "POINT_TRANSFER":
        displayType = "Balance Transfer";
        break;
      case "WITHDRAWAL":
        displayType = "Withdrawal";
        break;
      case "SYSTEM_TOPUP":
        displayType = "System Top-up";
        break;
      default:
        displayType = item.type;
    }

    return {
      ...item,
      type: displayType,
      status:
        item.status === "PENDING"
          ? intl.formatMessage({ id: "PENDING" })
          : item.status === "APPROVED"
          ? intl.formatMessage({ id: "APPROVED" })
          : intl.formatMessage({ id: "REJECTED" }),
      fromWallet: item.fromWallet?.systemUser?.name,
      toWallet: item.toWallet?.systemUser?.name,
    };
  });
  const currentUserId = authState.userProfile?.userName;
  const referralLink = currentUserId
    ? `${window.location.origin}/signup?sponsor=${currentUserId}`
    : "";
  const packageValue =
    walletTransactionsData?.getCurrentUserWallet?.systemUser?.package || "5";
  const points = walletTransactionsData?.getCurrentUserWallet?.point || 0;
  const balance = walletTransactionsData?.getCurrentUserWallet?.balance || 0;
  // Balance is already calculated (points * package), so we use it directly
  const totalAvailableBalance = balance;
  // Handle copy referral link
  const handleCopyReferralLink = async () => {
    if (!referralLink) {
      message.error(intl.formatMessage({ id: "Unable_To_Generate_Link" }));
      return;
    }

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyButtonText("COPIED");
      message.success(
        intl.formatMessage({ id: "Referral_Link_Copied_To_Clipboard" })
      );

      // Reset button text after 2 seconds
      setTimeout(() => {
        setCopyButtonText("COPY_LINK");
      }, 2000);
    } catch (error) {
      message.error(intl.formatMessage({ id: "FAILED_TO_COPY_LINK_TO_CLIPBOARD" }));
    }
  };

  const totalPendingPoints = formatedTableData
    .filter((item: any) => item.status === intl.formatMessage({ id: "PENDING" }))
    .reduce((acc: number, item: any) => {
      const amount = parseFloat(item.amount) || 0;
      return acc + amount;
    }, 0);

  // Get travel incentive points directly from the GraphQL query
  const totalIncentivePoints = totalIncentivePointsData?.getTotalIncentivePoints || 0;
  
  // Get total points earned directly from wallet
  const totalPointsEarned = walletTransactionsData?.getCurrentUserWallet?.point || 0;

  // Get team stats from GraphQL query
  const teamStats = teamData?.getMyTeam?.stats;
  const stats = [
    {
      title: "MY_TEAM",
      value: teamStats?.totalTeamSize || 0,
      prefix: <UserOutlined />,
      suffix: intl.formatMessage({ id: "USERS" }),
      color: "#3f8600",
    },
    {
      title: "MY_DIRECT",
      value: teamStats?.directReferrals || 0,
      prefix: <UserOutlined />,
      suffix: intl.formatMessage({ id: "USERS" }),
      color: "#1890ff",
    },
    {
      title: "AVAILABLE_BALANCE",
      value: `${String(getConfigurationsFromSession("default-currency") || "RM")} ${totalAvailableBalance || 0}`,
      suffix: "",
      color: "#52c41a",
    },
    {
      title: "TRAVEL_INCENTIVE_POINTS",
      value: totalPointsEarned || 0,
      prefix: <WalletOutlined />,
      suffix: "",
      color: "#722ed1",
    },
  ];

  // Wallet balance breakdown
  const walletBalances = [
    {
      title: "AVAILABLE_BALANCE", 
      amount: `${String(getConfigurationsFromSession("default-currency") || "RM")} ${totalAvailableBalance || 0}`,
      icon: <WalletOutlined style={{ color: "#52c41a" }} />,
      color: "#52c41a",
      description: "READY_FOR_WITHDRAWAL",
    },
    {
      title: "PENDING_BALANCE",
      amount: `${String(getConfigurationsFromSession("default-currency") || "RM")} ${totalPendingPoints || 0}`,
      icon: <ClockCircleOutlined style={{ color: "#faad14" }} />,
      color: "#faad14",
      description: "PROCESSING_TRANSACTIONS",
    },
    {
      title: "TOTAL_POINTS_EARNED",
      amount: totalPointsEarned || 0,
      icon: <DollarCircleOutlined style={{ color: "#1890ff" }} />,
      color: "#1890ff",
      description: "ALL_TIME_EARNINGS",
    },
    {
      title: "CATEGORY",
      amount: `${String(getConfigurationsFromSession("default-currency") || "RM")} ${packageValue || 0}`,
      icon: <CreditCardOutlined style={{ color: "#722ed1" }} />,
      color: "#722ed1",
      description: "YOUR_CURRENT_CATEGORY",
    },
  ];

  return (
    <div className="dashboard-container">
      <h1
        style={{
          marginBottom: "24px",
          fontSize: "28px",
          fontWeight: "bold",
          marginTop: "0",
        }}
      >
{intl.formatMessage({ id: "DASHBOARD_OVERVIEW" })}
      </h1>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card>
              <Statistic
                title={intl.formatMessage({ id: stat.title })}
                value={stat.value}
                prefix={stat.prefix}
                suffix={stat.suffix}
                valueStyle={{ color: stat.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Referral Link Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col span={24}>
          <Card
            title={
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <LinkOutlined />
                <span>{intl.formatMessage({ id: "MY_REFERAL_LINK" })}</span>
              </div>
            }
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <Text type="secondary">
                {intl.formatMessage({ id: "REFERRAL_DESCRIPTION" })}
              </Text>
              <div className="referral-link-container">
                <Input
                  value={referralLink}
                  readOnly
                  placeholder={
                    currentUserId
                      ? intl.formatMessage({ id: "GENERATING_REFERRAL_LINK" })
                      : intl.formatMessage({
                          id: "PLEASE_LOGIN_TO_GENERATE_REFERRAL_LINK",
                        })
                  }
                  className="referral-link-input"
                />
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={handleCopyReferralLink}
                  disabled={!referralLink}
                >
                  {intl.formatMessage({ id: copyButtonText })}
                </Button>
              </div>
              {currentUserId && (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {intl.formatMessage({ id: "YOUR_SPONSOR_ID" })}:{" "}
                  <Text code>{currentUserId}</Text>
                </Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Wallet Info */}
        <Col span={24}>
          <Card
            title={
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <WalletOutlined style={{ color: "#1890ff" }} />
                <span>{intl.formatMessage({ id: "MY_WALLET" })}</span>
              </div>
            }
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => navigate("/wallet/withdrawal")}
              >
                <TransactionOutlined /> {intl.formatMessage({ id: "View_All" })}
              </Button>
            }
            style={{ marginBottom: "16px" }}
          >
            {/* Wallet Balance Cards */}
            <Row gutter={[8, 8]} style={{ marginBottom: "16px" }}>
              {walletBalances.map((balance, index) => (
                <Col xs={24} sm={6} key={index}>
                  <div
                    style={{
                      background: "#fafafa",
                      padding: "12px",
                      borderRadius: "6px",
                      textAlign: "center",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>{balance.icon}</div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginBottom: "2px",
                      }}
                    >
                      {intl.formatMessage({ id: balance.title })}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: balance.color,
                      }}
                    >
                      {balance.amount}
                    </div>
                    <div style={{ fontSize: "9px", color: "#999" }}>
                      {intl.formatMessage({ id: balance.description })}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            <Divider style={{ margin: "12px 0" }}>
              {intl.formatMessage({ id: "RECENT_TRANSACTIONS" })}
            </Divider>

            {/* Recent Transactions */}
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
              <List
                itemLayout="horizontal"
                dataSource={formatedTableData}
                renderItem={(transaction: any) => (
                  <List.Item
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <List.Item.Meta
                      title={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "16px" }}>
                              {(() => {
                                switch(transaction.type) {
                                  case "Referral Bonus":
                                    return "🎁";
                                  case "Balance Transfer":
                                    return transaction.toWallet ? "📤" : "📥";
                                  case "Withdrawal":
                                    return "💳";
                                  case "System Top-up":
                                    return "⬆️";
                                  default:
                                    return "💰";
                                }
                              })()}
                            </span>
                            <div>
                              <div style={{ fontSize: "12px", fontWeight: "bold", color: "#333" }}>
                                {(() => {
                                  switch(transaction.type) {
                                    case "Referral Bonus":
                                      return "Referral Bonus";
                                    case "Balance Transfer":
                                      return transaction.toWallet ? "Transfer Sent" : "Transfer Received";
                                    case "Withdrawal":
                                      return "Withdrawal";
                                    case "System Top-up":
                                      return "Top-up";
                                    default:
                                      return transaction.type;
                                  }
                                })()}
                              </div>
                              <div style={{ fontSize: "10px", color: "#666" }}>
                                {(() => {
                                  switch(transaction.type) {
                                    case "Referral Bonus":
                                      return `From ${transaction.fromWallet || "System"}`;
                                    case "Balance Transfer":
                                      return transaction.toWallet 
                                        ? `To ${transaction.toWallet}`
                                        : `From ${transaction.fromWallet || "User"}`;
                                    case "Withdrawal":
                                      return "To external account";
                                    case "System Top-up":
                                      return "Admin top-up";
                                    default:
                                      return "";
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color:
                                  transaction.type === "Referral Bonus"
                                    ? "#52c41a"
                                    : transaction.type === "Balance Transfer"
                                    ? "#1890ff"
                                    : "#fa8c16",
                              }}
                            >
                              {transaction.type === "Referral Bonus" ? "+" : "-"}RM{" "}
                              {transaction.amount}
                            </span>
                            <Tag
                              color={
                                transaction.status ===
                                intl.formatMessage({ id: "APPROVED" })
                                  ? "success"
                                  : transaction.status ===
                                    intl.formatMessage({ id: "PENDING" })
                                  ? "warning"
                                  : "error"
                              }
                              style={{ fontSize: "10px", margin: 0 }}
                            >
                              {transaction.status ===
                                intl.formatMessage({ id: "APPROVED" }) && (
                                <CheckCircleOutlined />
                              )}
                              {transaction.status ===
                                intl.formatMessage({ id: "PENDING" }) && (
                                <ClockCircleOutlined />
                              )}
                              {transaction.status ===
                                intl.formatMessage({ id: "REJECTED" }) && (
                                <ExclamationCircleOutlined />
                              )}{" "}
                              {transaction.status?.toUpperCase()}
                            </Tag>
                          </div>
                        </div>
                      }
                      description={
                        <div style={{ fontSize: "10px", color: "#999" }}>
                          {new Date(transaction.createdAt).toLocaleString()}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>

            {/* Quick Actions */}
            <Divider style={{ margin: "12px 0" }}>
              {intl.formatMessage({ id: "QUICK_ACTIONS" })}
            </Divider>
            <Row gutter={[16, 16]} style={{ justifyContent: "space-around" }}>
              <Col xs={12} sm={6}>
                <Button
                  type="default"
                  block
                  size="small"
                  icon={<DownloadOutlined />}
                  style={{ fontSize: "11px", height: "32px" }}
                  onClick={() => navigate("/wallet/transfer")}
                >
                  {intl.formatMessage({ id: "WITHDRAW" })}
                </Button>
              </Col>
              <Col xs={12} sm={6}>
                <Button
                  type="default"
                  block
                  size="small"
                  icon={<TransactionOutlined />}
                  style={{ fontSize: "11px", height: "32px" }}
                  onClick={() => navigate("/wallet/withdrawal")}
                >
                  {intl.formatMessage({ id: "HISTORY" })}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Terms & Conditions Section */}
      <Row gutter={[16, 16]} style={{ marginTop: "24px" }}>
        <Col span={24}>
          <Card
            title={
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <FileTextOutlined style={{ color: "#1890ff" }} />
                <span>{intl.formatMessage({ id: "TNC" })}</span>
              </div>
            }
          >
            <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
              {String(getConfigurationsFromSession("terms-and-condition") || "").trim() ||
                intl.formatMessage({ id: "TNC_NOT_CONFIGURED" })}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
